/**
 * Google Translate compatibility workaround
 *
 * This prevents crashes when Google Translate modifies the DOM in ways that conflict with React.
 * Google Translate can move DOM nodes between different parent elements, which violates React's
 * assumptions about DOM structure and can cause crashes.
 *
 * This console logs the error instead of crashing. It may make the app slower.
 *
 * See: https://github.com/facebook/react/issues/11538
 *      https://martijnhols.nl/blog/everything-about-google-translate-crashing-react
 */

export function applyGoogleTranslateWorkaround(): void {
  // Only run in browser environment
  if (typeof window === 'undefined' || typeof Node !== 'function' || !Node.prototype) {
    return;
  }

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      if (console) {
        console.error('Cannot remove a child from a different parent', child, this);
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(newNode: T, referenceNode: Node | null): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (console) {
        console.error('Cannot insert before a reference node from a different parent', referenceNode, this);
      }
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}
